// app.luwu.dev/computers: the signed-in owner's computers, their tmux sessions,
// and full terminals in the browser. The browser registers itself as one of
// the account's devices (like the phone: its own Ed25519 key and a client
// certificate), reaches computers through the LA relay over WSS, and speaks
// the same ctl and term protocols as the app.

import type { Connection, Libp2p } from "@libp2p/interface";
import { generateKeyPair, privateKeyFromProtobuf, privateKeyToProtobuf } from "@libp2p/crypto/keys";
import { peerIdFromPrivateKey } from "@libp2p/peer-id";
import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import { create } from "@bufbuild/protobuf";
import { CreateWindowSchema, ListSessionsSchema, type Session as TmuxSession, type TermHost, type Window as TmuxWindow } from "@luwu/proto";
import { connectHost, ctlCall, openTerm, type Session } from "./luwu";
import { type Server, accountsBase, apiBase, chooseServer, currentServer, fetchServer, host as serverHost, offers } from "./server";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
const views = ["auth", "signup", "list", "connecting", "terminal"] as const;
function show(v: (typeof views)[number]) {
  for (const id of views) $(`view-${id}`).hidden = id !== v;
  $("authbar").hidden = v !== "auth" && v !== "signup";
  document.body.dataset.view = v;
}
const h = (tag: string, attrs: Record<string, string> = {}, ...kids: (Node | string | null)[]) => {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  for (const k of kids) if (k != null) e.append(k);
  return e;
};

const store = {
  get: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string | null) => {
    try {
      if (v === null) localStorage.removeItem(k);
      else localStorage.setItem(k, v);
    } catch {
      // private mode: this tab only
    }
  },
};
const TOKEN = "luwu-token"; // shared with /account
const DEVICE = "luwu-web-device";
// A device per server: a certificate from Luwu is no good on a company server.
const deviceKey = () => (currentServer() ? `${DEVICE}:${currentServer()!.api}` : DEVICE);
const b64 = (u: Uint8Array) => btoa(String.fromCharCode(...u));
const unb64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

let token = store.get(TOKEN);

class HttpError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}
async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(apiBase() + path, {
    method: init.method ?? "GET",
    headers: { authorization: `Bearer ${token}`, ...(init.body ? { "content-type": "application/json" } : {}) },
    body: init.body ? JSON.stringify(init.body) : undefined,
  });
  const data = (await res.json().catch(() => ({}))) as T & { message?: string; error?: string };
  if (!res.ok) throw new HttpError(res.status, data.message ?? data.error ?? `The server answered ${res.status}`);
  return data;
}

function signOut() {
  store.set(TOKEN, null);
  token = null;
  showAuth("login");
}

// ---------- logging in and signing up (Luwu accounts, or a company server's) ----------

// Luwu's Google client: One Tap and the Sign in with Google button. The
// account service accepts its ID tokens (POST /v1/auth/google).
const GOOGLE_CLIENT_ID = "527242319538-n3a8kkfesrlhlhddv7sd6qkelmqcvggd.apps.googleusercontent.com";
const DEVICE_NAME = "Luwu web";

interface SignupTicket {
  ticket: string;
  email: string;
  displayName?: string | null;
  suggestion?: string | null;
}

async function accountsPost<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(accountsBase() + path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
  } catch {
    throw new Error("Couldn't reach the sign-in server. Check your connection.");
  }
  const data = (await res.json().catch(() => ({}))) as T & { message?: string; error?: string };
  if (!res.ok) throw new Error(res.status === 429 ? "Too many attempts. Wait a minute and try again." : (data.message ?? data.error ?? `Sign-in failed (${res.status})`));
  return data;
}

let mode: "login" | "signup" = "login";
let email = "";
let pending: SignupTicket | null = null;

function showAuth(m: "login" | "signup") {
  mode = m;
  const srv = currentServer();
  $<HTMLFormElement>("f-server").hidden = true;
  $<HTMLFormElement>("f-code").hidden = true;
  $("auth-error").textContent = "";
  if (srv) {
    // A company server: only what it offers. Google is Luwu's own client.
    const sso = offers("oidc");
    const password = offers("password");
    const code = offers("email_code");
    $("auth-title").textContent = `Log in to ${srv.name}`;
    $("auth-sub").textContent = `Your company's Luwu server, at ${serverHost(srv.api)}.`;
    $("to-login").hidden = true;
    $("to-signup").hidden = true;
    $("g-button").hidden = true;
    $("sso").hidden = !sso;
    $("sso").textContent = `Continue with ${srv.oidcName}`;
    $("or").hidden = !(sso && (password || code));
    $<HTMLFormElement>("f-email").hidden = !(password || code);
    $("pw-label").hidden = !password;
    $("send-code").textContent = password ? "Log in" : "Email me a code";
    $("code-instead").hidden = !(password && code);
    if (!sso && !password && !code) $("auth-error").textContent = `${srv.name} offers no sign-in this console supports. Sign in with the Luwu app, or ask your administrator.`;
    $("auth-switch").replaceChildren(
      "Not your company's server? ",
      Object.assign(h("button", { type: "button", class: "linkbtn" }, "Use Luwu instead"), { onclick: () => useServer(null) }),
    );
    show("auth");
    return;
  }
  $("auth-title").textContent = m === "login" ? "Log in to Luwu" : "Create your Luwu account";
  $("auth-sub").textContent = m === "login" ? "Your computers' terminals, in the browser." : "One account for the Luwu app, the luwu command and this console.";
  $("auth-switch").replaceChildren(
    m === "login" ? "New to Luwu? " : "Already have an account? ",
    Object.assign(h("button", { type: "button", class: "linkbtn" }, m === "login" ? "Sign up" : "Log in"), { onclick: () => showAuth(m === "login" ? "signup" : "login") }),
    " · ",
    Object.assign(h("button", { type: "button", class: "linkbtn" }, "Sign in to a company server"), { onclick: showServerForm }),
  );
  $("to-login").hidden = m === "login";
  $("to-signup").hidden = m === "signup";
  $("g-button").hidden = false;
  $("or").hidden = false;
  $("sso").hidden = true;
  $<HTMLFormElement>("f-email").hidden = false;
  $("pw-label").hidden = true;
  $("send-code").textContent = "Email me a code";
  $("code-instead").hidden = true;
  show("auth");
  void googleIdentity();
}

// ---------- a company server (Luwu Enterprise) ----------

/** The server form: its address, checked against /.well-known/luwu.json. */
function showServerForm() {
  $("auth-title").textContent = "Sign in to a company server";
  $("auth-sub").textContent = "If your company runs its own Luwu server, enter its address. Your administrator has it.";
  $("to-login").hidden = true;
  $("to-signup").hidden = true;
  $("g-button").hidden = true;
  $("or").hidden = true;
  $("sso").hidden = true;
  $<HTMLFormElement>("f-email").hidden = true;
  $<HTMLFormElement>("f-code").hidden = true;
  $<HTMLFormElement>("f-server").hidden = false;
  $("server-error").textContent = "";
  $("auth-error").textContent = "";
  $("auth-switch").replaceChildren(Object.assign(h("button", { type: "button", class: "linkbtn" }, "Back to Luwu sign-in"), { onclick: () => showAuth("login") }));
  show("auth");
  $<HTMLInputElement>("server-url").focus();
}

/** Switches servers (null is Luwu). A token belongs to the server that issued it, so a switch signs out. */
function useServer(s: Server | null) {
  const before = apiBase();
  chooseServer(s);
  if (apiBase() !== before) {
    store.set(TOKEN, null);
    token = null;
  }
  applyServerNav();
  showAuth("login");
}

/** The nav on a company server: no Luwu usage or plans; a link to the server's own pages. */
function applyServerNav() {
  const srv = currentServer();
  $("nav-usage").hidden = !!srv;
  $("nav-plan").hidden = !!srv;
  const a = $<HTMLAnchorElement>("nav-server");
  a.hidden = !srv;
  if (srv) {
    a.href = srv.api;
    a.textContent = srv.name;
  }
}

$<HTMLFormElement>("f-server").addEventListener("submit", async (e) => {
  e.preventDefault();
  $("server-error").textContent = "";
  const btn = $<HTMLButtonElement>("server-continue");
  btn.disabled = true;
  try {
    useServer(await fetchServer($<HTMLInputElement>("server-url").value));
  } catch (x) {
    $("server-error").textContent = (x as Error).message;
  } finally {
    btn.disabled = false;
  }
});

// Single sign-on through the server's identity provider: the server sends a
// one-time code back here (?code=), redeemed at its account service.
$("sso").addEventListener("click", () => {
  location.href = `${accountsBase()}/v1/auth/oidc/start?${new URLSearchParams({ redirect: `${location.origin}/computers` })}`;
});
async function finishSSO(code: string) {
  history.replaceState(null, "", location.pathname);
  showAuth("login");
  try {
    await signedIn(await accountsPost("/v1/auth/token", { code, deviceName: DEVICE_NAME }));
  } catch (x) {
    $("auth-error").textContent = (x as Error).message;
  }
}
const ssoError = (reason: string) => {
  const name = currentServer()?.name ?? "the server";
  if (reason === "cancelled") return "Sign-in was cancelled.";
  if (reason === "expired") return "The sign-in expired. Try again.";
  if (reason === "not_allowed") return `Your account isn't allowed on ${name}. Ask your administrator.`;
  return `Sign-in at ${name} failed. Try again, or ask your administrator.`;
};

// /computers?next=/login?code=…: back there once signed in (kept across the Google trip).
const NEXT = "luwu-next";
{
  const n = new URLSearchParams(location.search).get("next");
  if (n?.startsWith("/") && !n.startsWith("//")) {
    try {
      sessionStorage.setItem(NEXT, n);
    } catch {
      // stays on this page
    }
  }
}
function afterSignIn() {
  let n: string | null = null;
  try {
    n = sessionStorage.getItem(NEXT);
    sessionStorage.removeItem(NEXT);
  } catch {
    // no storage
  }
  if (n) location.href = n;
  else void listComputers();
}

/** A token, or a new person who still chooses a username (and gives a name). */
async function signedIn(r: { token?: string; signup?: SignupTicket }) {
  if (r.token) {
    token = r.token;
    store.set(TOKEN, token);
    return afterSignIn();
  }
  if (!r.signup) throw new Error("Sign-in didn't finish. Try again.");
  pending = r.signup;
  $("signup-email").textContent = `For ${r.signup.email}.`;
  $<HTMLInputElement>("su-name").value = r.signup.displayName ?? "";
  $<HTMLInputElement>("su-user").value = r.signup.suggestion ?? "";
  updateAddr();
  $("su-error").textContent = "";
  show("signup");
  ($<HTMLInputElement>("su-name").value ? $<HTMLInputElement>("su-user") : $<HTMLInputElement>("su-name")).focus();
}

const updateAddr = () => ($("su-addr").textContent = `${$<HTMLInputElement>("su-user").value.trim().toLowerCase() || "you"}@ccnote.me`);
$("su-user").addEventListener("input", updateAddr);

async function sendCode() {
  $("auth-error").textContent = "";
  try {
    await accountsPost("/v1/auth/email/start", { email });
    $("code-sent").textContent = `We sent a 6-digit code to ${email}. It expires in 10 minutes.`;
    $<HTMLFormElement>("f-email").hidden = true;
    $<HTMLFormElement>("f-code").hidden = false;
    $<HTMLInputElement>("code").focus();
  } catch (x) {
    $("auth-error").textContent = (x as Error).message;
  }
}
$<HTMLFormElement>("f-email").addEventListener("submit", async (e) => {
  e.preventDefault();
  email = $<HTMLInputElement>("email").value.trim();
  if (!email.includes("@")) return void ($("auth-error").textContent = "Enter your email address.");
  if ($("pw-label").hidden) return sendCode();
  const pw = $<HTMLInputElement>("password");
  if (!pw.value) return void ($("auth-error").textContent = "Enter your password.");
  $("auth-error").textContent = "";
  try {
    await signedIn(await accountsPost("/v1/auth/password", { email, password: pw.value, deviceName: DEVICE_NAME }));
    pw.value = "";
  } catch (x) {
    $("auth-error").textContent = (x as Error).message;
  }
});
$("code-instead").addEventListener("click", () => {
  email = $<HTMLInputElement>("email").value.trim();
  if (!email.includes("@")) return void ($("auth-error").textContent = "Enter your email address.");
  void sendCode();
});
$<HTMLFormElement>("f-code").addEventListener("submit", async (e) => {
  e.preventDefault();
  $("auth-error").textContent = "";
  try {
    await signedIn(await accountsPost("/v1/auth/email/verify", { email, code: $<HTMLInputElement>("code").value.trim(), deviceName: DEVICE_NAME }));
  } catch (x) {
    $("auth-error").textContent = (x as Error).message;
  }
});
$("other-email").addEventListener("click", () => showAuth(mode));
$("to-login").addEventListener("click", () => showAuth("login"));
$("to-signup").addEventListener("click", () => showAuth("signup"));
$("su-cancel").addEventListener("click", () => ((pending = null), showAuth("signup")));
$<HTMLFormElement>("f-signup").addEventListener("submit", async (e) => {
  e.preventDefault();
  const name = $<HTMLInputElement>("su-name").value.trim();
  const username = $<HTMLInputElement>("su-user").value.trim().toLowerCase();
  if (!pending) return showAuth("signup");
  if (!name) return void ($("su-error").textContent = "Add your name.");
  try {
    const check = (await (await fetch(`${accountsBase()}/v1/auth/username-check?u=${encodeURIComponent(username)}`)).json()) as { ok: boolean; reason?: string };
    if (!check.ok) {
      $("su-error").textContent =
        check.reason === "taken" ? "That username is taken." : check.reason === "reserved" ? "That username is reserved." : "Use 3–30 letters, numbers, dots or dashes.";
      return;
    }
    const r = await accountsPost<{ token: string }>("/v1/auth/signup", { ticket: pending.ticket, username, deviceName: DEVICE_NAME });
    token = r.token;
    store.set(TOKEN, token);
    pending = null;
    await api("/v1/me", { method: "PUT", body: { display_name: name } }).catch(() => {});
    afterSignIn();
  } catch (x) {
    $("su-error").textContent = (x as Error).message;
  }
});

// Google Identity Services: One Tap (top right, when the browser is signed in
// to Google) and the Sign in with Google button.
interface Gis {
  initialize(o: object): void;
  prompt(): void;
  renderButton(el: HTMLElement, o: object): void;
  cancel(): void;
}
let gis: Promise<Gis | null> | null = null;
function googleIdentity(): Promise<Gis | null> {
  gis ??= new Promise((resolve) => {
    const sc = document.createElement("script");
    sc.src = "https://accounts.google.com/gsi/client";
    sc.async = true;
    sc.onload = () => {
      const id = (window as unknown as { google?: { accounts: { id: Gis } } }).google?.accounts.id ?? null;
      id?.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (r: { credential?: string }) => {
          if (!r.credential) return;
          $("auth-error").textContent = "";
          accountsPost<{ token?: string; signup?: SignupTicket }>("/v1/auth/google", { credential: r.credential, deviceName: DEVICE_NAME })
            .then(signedIn)
            .catch((x: Error) =>
              ($("auth-error").textContent = /not found/i.test(x.message) ? "Google sign-in isn't available right now. Use the email code instead." : x.message),
            );
        },
        auto_select: false,
        cancel_on_tap_outside: true,
        itp_support: true,
        use_fedcm_for_prompt: true,
        context: "signin",
      });
      resolve(id);
    };
    sc.onerror = () => resolve(null);
    document.head.append(sc);
  });
  return gis.then((id) => {
    if (!id) {
      $("g-button").hidden = true;
      $("or").hidden = true;
      return null;
    }
    $("g-button").replaceChildren();
    id.renderButton($("g-button"), { theme: "outline", size: "large", shape: "rectangular", text: mode === "login" ? "signin_with" : "signup_with", width: 320 });
    id.prompt();
    return id;
  });
}

// ---------- this browser as a device ----------

interface Device {
  key: string; // libp2p private key protobuf, base64
  device_id: string;
  cert: string;
  ca_keys: string;
  cert_expires_at: number;
  /** The team the cert carries ("" none); a new one is fetched when the account's team changes. */
  team?: string;
}

/** The account's team, from the computers list (team_id). */
let teamNow = "";

const browserName = () => {
  const ua = navigator.userAgent;
  const b = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${b} on ${os}` : b;
};

/**
 * The browser's device: its key, and a client certificate from the control
 * plane (registering again renews it; 7-day certs, renewed when 3 days are left).
 */
async function ensureDevice(): Promise<Device> {
  const saved = store.get(deviceKey());
  let d: Device | null = saved ? (JSON.parse(saved) as Device) : null;
  if (d && d.cert_expires_at - Date.now() / 1000 > 3 * 86400 && (d.team ?? "") === teamNow) return d;
  const register = async (keyB64: string) => {
    const key = privateKeyFromProtobuf(unb64(keyB64));
    const peer = peerIdFromPrivateKey(key).toString();
    const ts = Math.floor(Date.now() / 1000);
    const sig = b64(await key.sign(new TextEncoder().encode(`luwu-register-v1\n${peer}\n${ts}`)));
    const r = await api<{ device_id: string; cert: string; ca_keys: string; cert_expires_at: number }>("/v1/devices", {
      method: "POST",
      body: { peer_id: peer, name: browserName(), platform: "web", app_version: "web/0.2.0", ts, sig },
    });
    return { key: keyB64, device_id: r.device_id, cert: r.cert, ca_keys: r.ca_keys, cert_expires_at: r.cert_expires_at, team: teamNow };
  };
  const fresh = async () => b64(privateKeyToProtobuf(await generateKeyPair("Ed25519")));
  try {
    d = await register(d?.key ?? (await fresh()));
  } catch (e) {
    // This key belongs to another account (signed in as someone else): start over.
    if (!(e instanceof HttpError && e.status === 409)) throw e;
    d = await register(await fresh());
  }
  store.set(deviceKey(), JSON.stringify(d));
  return d;
}

// ---------- computers ----------

interface Computer {
  id: string;
  peer_id: string;
  kind: string;
  name: string;
  platform: string;
  online: boolean;
  last_seen_at: number | null;
  /** A teammate's computer shared with the team. */
  shared?: boolean;
  owner_name?: string;
}

const ago = (unix: number | null) => {
  if (!unix) return "never";
  const s = Date.now() / 1000 - unix;
  return s < 120 ? "just now" : s < 3600 ? `${Math.round(s / 60)} min ago` : s < 86400 ? `${Math.round(s / 3600)} h ago` : `${Math.round(s / 86400)} d ago`;
};

let current: { computer: Computer; node: Libp2p; conn: Connection; sessions: TmuxSession[] } | null = null;

async function showWho() {
  try {
    const me = await api<{ username: string | null; email: string | null; display_name: string | null }>("/v1/me");
    $("who").textContent = me.display_name || me.username || me.email || "";
    $("who").title = [me.display_name, me.email].filter(Boolean).join(" · ");
  } catch {
    // the list still works
  }
}

async function listComputers() {
  show("list");
  void gis?.then((id) => id?.cancel());
  void showWho();
  $("list-title").textContent = "Your computers";
  $("list-sub").textContent = "";
  $("list-error").textContent = "";
  $("list-body").replaceChildren(h("p", {}, "Loading…"));
  let all: Computer[];
  try {
    const r = await api<{ devices: Computer[]; team_id?: string }>("/v1/devices");
    all = r.devices;
    teamNow = r.team_id ?? "";
  } catch (e) {
    if (e instanceof HttpError && e.status === 401) return signOut();
    $("list-body").replaceChildren();
    $("list-error").textContent = (e as Error).message;
    return;
  }
  const computers = all.filter((d) => d.kind === "host").sort((a, b) => Number(b.online) - Number(a.online) || (b.last_seen_at ?? 0) - (a.last_seen_at ?? 0));
  if (!computers.length) {
    $("list-body").replaceChildren(h("div", { class: "list" }, h("p", { class: "empty" }, currentServer()
          ? `No computers yet. On a Mac or Linux computer run curl -fsSL https://luwu.dev/install.sh | sh, then luwu login --server ${currentServer()!.api} and luwu up, signed in with the same account.`
          : "No computers yet. On a Mac or Linux computer run curl -fsSL https://luwu.dev/install.sh | sh, then luwu, and sign in with the same account.")));
    return;
  }
  const rows = computers.map((c) => {
    const b = h(
      "button",
      { class: "row", type: "button" },
      h("span", { class: c.online ? "dot on" : "dot" }),
      h("span", { class: "main" }, h("span", { class: "name" }, c.name || c.id), h("span", { class: "sub" }, (c.online ? `online · ${c.platform}` : `offline · seen ${ago(c.last_seen_at)}`) + (c.shared ? ` · shared by ${c.owner_name || "a teammate"}` : ""))),
      "›",
    ) as HTMLButtonElement;
    if (!c.online) b.disabled = true;
    b.addEventListener("click", () => void openComputer(c));
    return b;
  });
  $("list-body").replaceChildren(h("h2", {}, "Computers"), h("div", { class: "list" }, ...rows));
  $("list-sub").textContent = `Terminals open here as this browser (${browserName()}), one of your devices. Remove it any time under Devices in the app.`;
}

async function disconnect() {
  const c = current;
  current = null;
  try {
    await c?.node.stop();
  } catch {
    // already stopped
  }
}

async function openComputer(c: Computer) {
  show("connecting");
  $("step").textContent = "Setting up this browser…";
  try {
    const d = await ensureDevice();
    await disconnect();
    const key = privateKeyFromProtobuf(unb64(d.key));
    const steps: Record<string, string> = { relay: `Reaching ${c.name}…`, auth: "Checking its identity…", terminal: "Opening…" };
    const { node, conn } = await connectHost(key, { cert: d.cert, ca_keys: d.ca_keys, host: { peer_id: c.peer_id, name: c.name } }, (s) => ($("step").textContent = steps[s] ?? "…"));
    current = { computer: c, node, conn, sessions: [] };
    await listWindows();
  } catch (e) {
    if (e instanceof HttpError && e.status === 401) return signOut();
    await listComputers();
    $("list-error").textContent = `${c.name}: ${(e as Error).message}`;
  }
}

async function loadSessions(): Promise<TmuxSession[]> {
  const r = await ctlCall(current!.conn, { case: "listSessions", value: create(ListSessionsSchema, {}) });
  const sessions = r.resp.case === "sessions" ? r.resp.value.sessions : [];
  current!.sessions = sessions;
  return sessions;
}

async function listWindows() {
  const c = current!;
  show("list");
  $("list-title").textContent = c.computer.name;
  $("list-sub").textContent = "";
  $("list-error").textContent = "";
  const back = h("button", { class: "linkbtn", type: "button" }, "‹ All computers");
  back.addEventListener("click", () => void disconnect().then(listComputers));
  $("list-body").replaceChildren(back, h("p", {}, "Loading sessions…"));
  let sessions: TmuxSession[];
  try {
    sessions = await loadSessions();
  } catch (e) {
    $("list-body").replaceChildren(back);
    $("list-error").textContent = (e as Error).message;
    return;
  }
  const form = newSessionForm(sessions);
  if (!sessions.length) {
    $("list-body").replaceChildren(back, form, h("div", { class: "list" }, h("p", { class: "empty" }, "No tmux sessions on this computer yet. Start one above.")));
    return;
  }
  const blocks = sessions.flatMap((s) => [
    h(
      "div",
      { class: "sesshead" },
      h("h2", {}, s.name),
      Object.assign(h("button", { type: "button", class: "linkbtn small" }, "+ New window"), { onclick: () => void createWindow(s.id, "", "") }),
    ),
    h(
      "div",
      { class: "list" },
      ...s.windows.map((w) => {
        const pane = w.panes.find((p) => p.active) ?? w.panes[0];
        const b = h(
          "button",
          { class: "row", type: "button" },
          h("span", { class: "main" }, h("span", { class: "name" }, `${w.index}: ${w.name}`), h("span", { class: "sub" }, [pane?.command, pane?.cwd].filter(Boolean).join("  "))),
          "›",
        );
        b.addEventListener("click", () => void openWindow(s, w));
        return b;
      }),
    ),
  ]);
  $("list-body").replaceChildren(back, form, ...blocks);
}

// tmux can't use ':' or '.' in names; the computer checks too.
const nameOk = (n: string) => n.length <= 64 && !/[:.]/.test(n);

/** "New session": a name, what to start in it, and Create (then it opens). */
function newSessionForm(sessions: TmuxSession[]) {
  const name = h("input", { id: "ns-name", placeholder: "Session name, like api or blog", maxlength: "64", autocomplete: "off", spellcheck: "false" }) as HTMLInputElement;
  const preset = h(
    "select",
    { id: "ns-preset", "aria-label": "Start" },
    h("option", { value: "" }, "Shell"),
    h("option", { value: "claude" }, "Claude Code"),
    h("option", { value: "codex" }, "Codex"),
    h("option", { value: "gemini" }, "Gemini"),
  ) as HTMLSelectElement;
  const err = h("div", { class: "error", role: "alert" });
  const f = h("form", { class: "newsess", novalidate: "" }, name, preset, h("button", { class: "btn primary small", type: "submit" }, "New session"), err) as HTMLFormElement;
  f.addEventListener("submit", (e) => {
    e.preventDefault();
    const n = name.value.trim();
    if (!n) return void (err.textContent = "Give it a name.");
    if (!nameOk(n)) return void (err.textContent = "Names can't contain ':' or '.'.");
    if (sessions.some((s) => s.name === n)) return void (err.textContent = `There's already a session named ${n}; use + New window there.`);
    err.textContent = "";
    void createWindow(n, "", preset.value).catch((x: Error) => (err.textContent = x.message));
  });
  return f;
}

/** Opens a window in a session (created if missing) and switches to it. */
async function createWindow(session: string, name: string, preset: string) {
  const c = current;
  if (!c) return;
  try {
    const r = await ctlCall(c.conn, { case: "createWindow", value: create(CreateWindowSchema, { session, name, preset }) });
    const wid = r.resp.case === "window" ? r.resp.value.id : "";
    const sessions = await loadSessions();
    const s = sessions.find((x) => x.windows.some((w) => w.id === wid)) ?? sessions.find((x) => x.name === session || x.id === session);
    const w = s?.windows.find((x) => x.id === wid);
    if (s && w) return openWindow(s, w);
    await listWindows();
  } catch (e) {
    $("list-error").textContent = (e as Error).message;
    throw e;
  }
}

// ---------- the terminal ----------

let term: Terminal | null = null;
let fitAddon: FitAddon | null = null;
let session: Session | null = null;
let observer: ResizeObserver | null = null;

async function closeTerm() {
  const s = session;
  session = null;
  await s?.close().catch(() => {});
}

function renderTabs(s: TmuxSession, active: string) {
  $("tabs").replaceChildren(
    ...s.windows.map((w) => {
      const b = h("button", { type: "button", role: "tab", "aria-current": String(w.id === active) }, `${w.index}:${w.name}`);
      b.addEventListener("click", () => w.id !== active && void openWindow(s, w));
      return b;
    }),
  );
}

async function openWindow(s: TmuxSession, w: TmuxWindow) {
  const c = current;
  if (!c) return;
  await closeTerm();
  show("terminal");
  $("t-title").textContent = `${c.computer.name} · ${s.name}:${w.index} ${w.name}`;
  $("t-path").textContent = "connecting…";
  renderTabs(s, w.id);
  if (!term) {
    term = new Terminal({
      fontFamily: '"IBM Plex Mono", ui-monospace, Menlo, Consolas, monospace',
      fontSize: 14,
      cursorBlink: true,
      scrollback: 5000,
      theme: { background: "#0E1525", foreground: "#E6E2D6", cursor: "#C9A35A", selectionBackground: "#C9A35A55" },
    });
    fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    await Promise.race([document.fonts.load('14px "IBM Plex Mono"').catch(() => {}), new Promise((r) => setTimeout(r, 1500))]);
    term.open($("term"));
    const enc = new TextEncoder();
    term.onData((d) => session?.input(enc.encode(d)));
    term.onBinary((d) => session?.input(Uint8Array.from(d, (ch) => ch.charCodeAt(0))));
    observer = new ResizeObserver(() => {
      fitAddon?.fit();
      if (term) session?.resize(term.cols, term.rows);
    });
    observer.observe($("term"));
  }
  term.reset();
  fitAddon!.fit();
  const onMessage = (m: TermHost) => {
    switch (m.m.case) {
      case "data":
        term?.write(m.m.value.data);
        break;
      case "exit":
        $("t-path").textContent = m.m.value.reason || "closed";
        break;
    }
  };
  try {
    session = await openTerm(c.node, c.conn, { sessionId: s.id, windowId: w.id, cols: term.cols, rows: term.rows }, onMessage, false);
  } catch (e) {
    $("t-path").textContent = (e as Error).message;
    return;
  }
  // This browser sizes the window while it's in use, like "fit" in the app.
  session.fit(true);
  session.resize(term.cols, term.rows);
  $("t-path").textContent = session.path;
  term.focus();
}

$("back").addEventListener("click", () => void closeTerm().then(listWindows));
$("sign-out").addEventListener("click", () => void disconnect().then(signOut));
window.addEventListener("pagehide", () => void disconnect());

applyServerNav();
{
  const q = new URLSearchParams(location.search);
  if (q.get("code") && currentServer()) void finishSSO(q.get("code")!);
  else if (q.get("error") && currentServer()) {
    history.replaceState(null, "", location.pathname);
    showAuth("login");
    $("auth-error").textContent = ssoError(q.get("error")!);
  } else if (token) void listComputers();
  else showAuth(q.has("signup") ? "signup" : "login");
}
